const THREAD_SEPARATOR_RE = /\n---\n|\n---$/;
const NUMBERED_TWEET_RE = /^\d+\//m;
const HASHTAG_RE = /#\w+/g;

export function hasThreadMarkers(content: string): boolean {
  return THREAD_SEPARATOR_RE.test(content) || NUMBERED_TWEET_RE.test(content);
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/gs, "$1")
    .replace(/\*(.+?)\*/gs, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/`(.+?)`/gs, "$1")
    .trim();
}

function extractHashtags(text: string): string[] {
  return (text.match(HASHTAG_RE) ?? []);
}

function removeHashtags(text: string): string {
  return text
    .replace(HASHTAG_RE, "")
    .replace(/[^\S\n]+/g, " ")
    .replace(/\n /g, "\n")
    .replace(/ \n/g, "\n")
    .trim();
}

function normalizeDoubleLineBreaks(text: string): string {
  return text.replace(/(?<!\n)\n(?!\n)/g, "\n\n");
}

export function formatForLinkedIn(content: string): string {
  const stripped = stripMarkdown(content);
  const hashtags = extractHashtags(stripped);
  let body = removeHashtags(stripped);
  body = normalizeDoubleLineBreaks(body).trim();
  const cappedTags = [...new Set(hashtags)].slice(0, 5);
  if (cappedTags.length === 0) return body;
  return body + "\n\n" + cappedTags.join(" ");
}

export function formatForEmail(content: string): string {
  let result = stripMarkdown(content);
  result = removeHashtags(result);
  result = result.replace(/\n{3,}/g, "\n\n");
  if (!/\n\n/.test(result)) {
    result = normalizeDoubleLineBreaks(result);
  }
  return result.trim();
}

export function formatForSlack(content: string): string {
  let result = content
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/gs, "*$1*")
    .replace(/`(.+?)`/gs, "`$1`")
    .replace(/^-\s+/gm, "- ")
    .replace(/^\*\s+/gm, "- ");
  result = removeHashtags(result);
  return result.trim();
}

export function formatForMarkdown(content: string): string {
  return content;
}

export function copyForPlatform(content: string, platform?: string): string {
  if (!platform) return content;

  switch (platform) {
    case "linkedin":
      return formatForLinkedIn(content);
    case "email":
      return formatForEmail(content);
    case "slack":
      return formatForSlack(content);
    case "markdown":
      return formatForMarkdown(content);
    case "reddit":
      return content;
    case "twitter":
    case "instagram":
    case "facebook":
    default:
      return stripMarkdown(content);
  }
}

export function copyAsThread(content: string): string {
  let tweets: string[];

  if (THREAD_SEPARATOR_RE.test(content)) {
    tweets = content.split(THREAD_SEPARATOR_RE).map((s) => s.trim()).filter(Boolean);
  } else {
    tweets = content.split(/\n+(?=\d+\/)/).map((s) => s.trim()).filter(Boolean);
  }

  return tweets
    .map((tweet, i) => {
      const clean = tweet.replace(/^\d+\/\s*/, "").trim();
      return `${i + 1}/ ${clean}`;
    })
    .join("\n\n");
}
